// カード配車の mutation（ダイヤの呼び出し・対応づけ・ダイヤとして保存・固定コース付与。log_web_issues#365）
import { QueryClient, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { TimetableDetailEntity, TimetableDetailResponse, deserializeTimetableDetail } from 'src/entities/Timetable.entity';
import { CONVENIENCE_DISPATCH_ALL_DELIVERIES_QUERY_KEY, CONVENIENCE_DISPATCH_DELIVERIES_QUERY_KEY } from 'src/hooks/useConvenienceDispatchDeliveries.query';
import { invalidateCourseMaster } from 'src/hooks/useCourseMutations';
import { SHIPMENT_ORDER_LEG_RUNS_QUERY_KEY } from 'src/hooks/useQueryShipmentOrderLegRuns';
import { DailyTimetableEntity, dailyTimetableQueryKey, waitForDailyTimetableReplaces } from 'src/hooks/useSelectedTimetableId';
import { TIMETABLES_BASE_PATH, timetableQueryKey } from 'src/hooks/useTimetable.query';
import { fetchTimetableTripGroups, timetableTripGroupsQueryKey } from 'src/hooks/useTimetableTripGroups.query';

const FIXED_COURSE_ASSIGNMENTS_PATH = '/api/v5/convenience_dispatches/fixed_course_assignments';

// 要確認の伝票を候補のトラックに置いたとき、そのトラックの固定コースを付ける（POST /convenience_dispatches/fixed_course_assignments。#363）。
// 付いた固定コースは leg run 一覧（バッジ・カード）と、deliveries の rotations（現在コースの多数決）に出るので両方捨てる。
// Why not setQueryData: 付与後の値は API が決める（付け替えもある）
export const useAssignFixedCourseMutation = () => {
  const queryClient = useQueryClient();
  return useMutation<{ updatedShipmentOrderIds: number[] }, Error, { planningDate: string; legRunIds: number[]; truckId: number }>({
    mutationFn: async ({ planningDate, legRunIds, truckId }) => {
      const res = await axios.post<{ updated_shipment_order_ids: number[] }>(FIXED_COURSE_ASSIGNMENTS_PATH, {
        data: { planning_date: planningDate, leg_run_ids: legRunIds, truck_id: truckId },
      });
      return { updatedShipmentOrderIds: res.data.updated_shipment_order_ids };
    },
    onSuccess: async ({ updatedShipmentOrderIds }) => {
      if (updatedShipmentOrderIds.length === 0) return;
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [SHIPMENT_ORDER_LEG_RUNS_QUERY_KEY] }),
        queryClient.invalidateQueries({ queryKey: [CONVENIENCE_DISPATCH_DELIVERIES_QUERY_KEY] }),
        queryClient.invalidateQueries({ queryKey: [CONVENIENCE_DISPATCH_ALL_DELIVERIES_QUERY_KEY] }),
      ]);
    },
  });
};

// ---- ダイヤの呼び出し・対応づけ（POST /api/v5/timetables/:id/applications。log_web_issues#365）----

export type ApplyNeedsReviewCandidate = { courseId: number; courseCode: string; truckId: number | null; rotationNo: number };
export type ApplyNeedsReviewStore = { storeName: string; legRunCount: number; candidates: ApplyNeedsReviewCandidate[] };
/** トラックの無い便グループ（反映で飛ばした）。code は 'truck_missing' */
export type ApplyTimetableWarning = { code: string; courseId: number; courseCode: string; tripNo: number };
/** API の 200 の形そのまま（docs/api_v5_timetable_applications.md）。キャッシュ更新に使う id・日付は variables から取る */
export type ApplyTimetableResult = {
  assignedCount: number;
  assignedLegRunCount: number;
  unassignedCount: number;
  noDeliveryCount: number;
  fixedCourseCount: number;
  needsReviewStores: ApplyNeedsReviewStore[];
  warnings: ApplyTimetableWarning[];
};

type ApplyTimetableResponse = {
  assigned_count: number;
  assigned_leg_run_count?: number;
  unassigned_count: number;
  no_delivery_count: number;
  fixed_course_count?: number;
  needs_review_stores?: { store_name: string; leg_run_count: number; candidates: { course_id: number; course_code: string; truck_id: number | null; rotation_no: number }[] }[];
  warnings?: { code: string; course_id: number; course_code: string; trip_no: number }[];
};

const deserializeApplyResult = (data: ApplyTimetableResponse): ApplyTimetableResult => ({
  assignedCount: data.assigned_count,
  assignedLegRunCount: data.assigned_leg_run_count ?? 0,
  unassignedCount: data.unassigned_count,
  noDeliveryCount: data.no_delivery_count,
  fixedCourseCount: data.fixed_course_count ?? 0,
  needsReviewStores: (data.needs_review_stores ?? []).map((store) => ({
    storeName: store.store_name,
    legRunCount: store.leg_run_count,
    candidates: store.candidates.map((c) => ({ courseId: c.course_id, courseCode: c.course_code, truckId: c.truck_id, rotationNo: c.rotation_no })),
  })),
  warnings: (data.warnings ?? []).map((w) => ({ code: w.code, courseId: w.course_id, courseCode: w.course_code, tripNo: w.trip_no })),
});

/**
 * ダイヤの呼び出し（createOperations: false = 当日を更地化して作り直す）と対応づけ（true = 地点と案件を店舗名で突き合わせて operation を作る）。
 * その日のダイヤは API が同じ transaction で書くので、FE から PUT しない（設計書 決定 4）。
 * 成功後の同期は hook 側の onSuccess で await し、終わるまで isPending を続ける（Backdrop が閉じない）。
 * 対象日全件のクエリ（運賃計算用。未選択からの呼び出しでは daily の書き換えで初めて有効になる）は待たない:
 * 行の材料ではなく、取得中は運賃計算の判定（resolveConvenienceTariffAvailability）が isFetching を見てボタンを止める。
 * Why not mutate 単位の onSuccess: TanStack v5 は unmount 後に mutate 単位のコールバックを捨てる
 */
export const useApplyTimetableMutation = () => {
  const queryClient = useQueryClient();
  return useMutation<ApplyTimetableResult, Error, { timetableId: number; planningDate: string; createOperations?: boolean }>({
    mutationFn: async ({ timetableId, planningDate, createOperations }) => {
      // 選び直しの PUT・同じ日のダイヤとして保存が残っていれば、それが DB に入ってから反映する（反映が書く値を後から上書きさせない）
      await waitForDailyTimetableReplaces(queryClient, planningDate);
      await waitForTimetableSnapshotSaves(queryClient, planningDate);
      const res = await axios.post<ApplyTimetableResponse>(`/api/v5/timetables/${timetableId}/applications`, {
        data: { planning_date: planningDate, create_operations: createOperations ?? false },
      });
      return deserializeApplyResult(res.data);
    },
    onSuccess: async (_result, { timetableId, planningDate }) => {
      // API が書いた「その日のダイヤ」に合わせる。取り直しが返るまでの表示用
      queryClient.setQueryData<DailyTimetableEntity>(dailyTimetableQueryKey(planningDate), (prev) => ({
        planningDate, timetableId, updatedAt: prev?.updatedAt ?? null,
      }));
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: dailyTimetableQueryKey(planningDate) }),
        // 反映したダイヤの便グループは、未選択から呼び出したときはまだ active でない（invalidate は active な query しか取り直さない）。
        // 先読みして、Backdrop が閉じる前に要確認・本日なし・ダイヤ外の材料をそろえる。失敗しても反映は成功しているので握りつぶす。
        // Why not ['timetables', 'tripGroups'] 配下の invalidate を重ねる: 既定の cancelRefetch で先読みが取り消されて GET が 2 回走る。
        // 便グループはダイヤの構造（コース・地点・トラック）で、反映では変わらない。古いキャッシュは staleTime: 0 で取り直す
        queryClient.fetchQuery({
          queryKey: timetableTripGroupsQueryKey(timetableId, planningDate),
          queryFn: () => fetchTimetableTripGroups(timetableId, planningDate),
          staleTime: 0,
        }).catch(() => undefined),
        queryClient.invalidateQueries({ queryKey: [CONVENIENCE_DISPATCH_DELIVERIES_QUERY_KEY] }),
        queryClient.invalidateQueries({ queryKey: [CONVENIENCE_DISPATCH_ALL_DELIVERIES_QUERY_KEY] }),
        queryClient.invalidateQueries({ queryKey: [SHIPMENT_ORDER_LEG_RUNS_QUERY_KEY] }),
      ]);
    },
  });
};

// ---- ダイヤとして保存（POST / PATCH /api/v5/master/timetables(/:id)/snapshots。log_web_issues#364・#365）----

/** 保存は止めない知らせ（type: truck_mismatch / course_duplicated / trip_no_taken）。message をそのまま出す */
export type TimetableSnapshotWarning = { type: string; message: string };
export type TimetableSnapshotResult = { timetable: TimetableDetailEntity; warnings: TimetableSnapshotWarning[] };

export const deserializeSnapshotWarnings = (data: unknown): TimetableSnapshotWarning[] =>
  Array.isArray(data)
    ? data
      .filter((w): w is Record<string, unknown> => w != null && typeof w === 'object')
      .map((w) => ({ type: typeof w.type === 'string' ? w.type : '', message: typeof w.message === 'string' ? w.message : '' }))
    : [];

type SnapshotResponse = TimetableDetailResponse & { warnings?: unknown };
const toSnapshotResult = (data: SnapshotResponse): TimetableSnapshotResult => ({
  timetable: deserializeTimetableDetail(data), warnings: deserializeSnapshotWarnings(data.warnings),
});

// 新規保存・上書きで共通。呼び出し・対応づけが同じ日の保存を待つのに使う
const SNAPSHOT_SAVE_MUTATION_KEY = ['timetableSnapshot', 'save'] as const;

const isSavingSnapshotOn = (queryClient: QueryClient, date: string) => queryClient.isMutating({
  mutationKey: SNAPSHOT_SAVE_MUTATION_KEY,
  predicate: (mutation) => (mutation.state.variables as { planningDate?: string } | undefined)?.planningDate === date,
}) > 0;

/**
 * 同じ日のダイヤとして保存が全部終わるまで待つ。反映 API の前に呼ぶ（log_web_issues#403 の 2）。
 * Why not 待たずに送る: 保存 API もその日のダイヤを書くので、保存の要求が遅れて届くと、ダイアログを閉じた後に
 * 呼び出したダイヤを保存が上書きする（会社の行のロックは届いた順にしか並ばない）
 */
export const waitForTimetableSnapshotSaves = (queryClient: QueryClient, date: string): Promise<void> => {
  if (!isSavingSnapshotOn(queryClient, date)) return Promise.resolve();
  return new Promise((resolve) => {
    const unsubscribe = queryClient.getMutationCache().subscribe(() => {
      if (isSavingSnapshotOn(queryClient, date)) return;
      unsubscribe();
      resolve();
    });
  });
};

// 保存は仮コースを作り、既存コースの所属・ルートも変える（C8）ので、ダイヤ一覧だけでなくコースマスタ全体を捨てる。
// 便グループ（['timetables', 'tripGroups']）も一覧のキー配下なので一緒に捨てる。
// その日の案件の固定コースも書く（log_web_issues#403 の 2）ので、配車（通常・全件）と案件も取り直す
const useRefreshAfterSnapshot = () => {
  const queryClient = useQueryClient();
  return async (result: TimetableSnapshotResult) => {
    queryClient.setQueryData(timetableQueryKey(result.timetable.id), result.timetable);
    await Promise.all([
      invalidateCourseMaster(queryClient),
      queryClient.invalidateQueries({ queryKey: [CONVENIENCE_DISPATCH_DELIVERIES_QUERY_KEY] }),
      queryClient.invalidateQueries({ queryKey: [CONVENIENCE_DISPATCH_ALL_DELIVERIES_QUERY_KEY] }),
      queryClient.invalidateQueries({ queryKey: [SHIPMENT_ORDER_LEG_RUNS_QUERY_KEY] }),
    ]);
  };
};

/**
 * 新規保存。API が同じ transaction でその日のダイヤを保存したダイヤにする（log_web_issues#403 の 2）ので、PUT せずに取り直す。
 * Why not 応答の id を setQueryData: 保存の後に別のダイヤを呼び出していれば、DB の値はそちら。取り直しで DB に合わせる
 */
export const useSaveTimetableSnapshotMutation = () => {
  const queryClient = useQueryClient();
  const refresh = useRefreshAfterSnapshot();
  return useMutation<TimetableSnapshotResult, Error, { planningDate: string; name: string }>({
    mutationKey: SNAPSHOT_SAVE_MUTATION_KEY,
    mutationFn: async ({ planningDate, name }) => toSnapshotResult(
      (await axios.post<SnapshotResponse>(`${TIMETABLES_BASE_PATH}/snapshots`, { data: { planning_date: planningDate, name } })).data,
    ),
    onSuccess: async (result, { planningDate }) => {
      await Promise.all([refresh(result), queryClient.invalidateQueries({ queryKey: dailyTimetableQueryKey(planningDate) })]);
    },
  });
};

export const useOverwriteTimetableSnapshotMutation = () => {
  const refresh = useRefreshAfterSnapshot();
  return useMutation<TimetableSnapshotResult, Error, { planningDate: string; timetableId: number }>({
    mutationKey: SNAPSHOT_SAVE_MUTATION_KEY,
    mutationFn: async ({ planningDate, timetableId }) => toSnapshotResult(
      (await axios.patch<SnapshotResponse>(`${TIMETABLES_BASE_PATH}/${timetableId}/snapshots`, { data: { planning_date: planningDate } })).data,
    ),
    onSuccess: refresh,
  });
};
